import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import { db } from '../firebaseconfig';
import './AskExpertPage.css';
import '../styles/form-theme.css';

const AskExpertPage = () => {
  const navigate = useNavigate();

  const [question, setQuestion] = useState('');
  const [studentName, setStudentName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState({ kind: '', text: '' });

  const canSubmit = useMemo(() => {
    const trimmedQuestion = question.trim();
    if (trimmedQuestion.length < 15 || trimmedQuestion.length > 800) return false;

    if (!isAnonymous) {
      if (!studentName.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return false;
    }

    if (contact.trim() && !/^[\d+\-\s]{10,15}$/.test(contact.trim())) return false;

    return true;
  }, [question, isAnonymous, studentName, email, contact]);

  const resetForm = () => {
    setQuestion('');
    setStudentName('');
    setContact('');
    setEmail('');
    setIsAnonymous(true);
  };

  const buildActorPayload = () => ({
    name: studentName.trim() || 'Anonymous Visitor',
    email: email.trim() || 'not-provided',
    contact: contact.trim() || 'not-provided',
    isAnonymous: !studentName.trim() && !email.trim() && !contact.trim(),
  });

  const saveMetricEvent = async (metricType, metricLabel) => {
    const actor = buildActorPayload();

    try {
      await addDoc(collection(db, 'submittedQuestions'), {
        question: `CTA Metric: ${metricLabel}`,
        name: actor.name,
        email: actor.email,
        contact: actor.contact,
        isAnonymous: actor.isAnonymous,
        source: 'ask-expert-page',
        metricType,
        submittedAt: new Date().toISOString(),
        createdAt: serverTimestamp(),
      });
    } catch (_) {
      // Metrics should not block the primary CTA navigation.
    }
  };

  const submitQuestion = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setNotice({ kind: '', text: '' });
    setIsSubmitting(true);

    const payload = {
      question: question.trim(),
      askedBy: isAnonymous ? 'Anonymous Student' : studentName.trim(),
      replyEmail: isAnonymous ? 'not-provided' : email.trim(),
      contact: contact.trim() || 'not-provided',
      isAnonymous,
      source: 'ask-expert-page',
      submittedAt: new Date().toISOString(),
    };

    try {
      await addDoc(collection(db, 'submittedQuestions'), {
        question: payload.question,
        name: payload.askedBy,
        email: payload.replyEmail,
        contact: payload.contact,
        isAnonymous: payload.isAnonymous,
        source: payload.source,
        submittedAt: payload.submittedAt,
        createdAt: serverTimestamp(),
      });

      setNotice({
        kind: 'success',
        text: 'Question submitted successfully. Our expert panel will review it shortly.',
      });
      resetForm();
    } catch (error) {
      const existing = JSON.parse(localStorage.getItem('algodoor_expert_questions') || '[]');
      existing.push(payload);
      localStorage.setItem('algodoor_expert_questions', JSON.stringify(existing));

      setNotice({
        kind: 'warning',
        text: error?.message
          ? `Failed to save on Firebase. Saved locally as backup. (${error.message})`
          : 'Failed to save on Firebase. Saved locally as backup.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="ask-expert-page">
      <SEOMeta
        title="Ask an Expert | Algodoor"
        description="Submit your technical questions directly to Algodoor experts and speaker mentors."
        keywords="ask expert question, student tech questions, javascript mentor, react mentor"
        url="/ask-expert"
        type="WebPage"
        noindex
      />

      <section className="ask-expert-hero">
        <button type="button" className="ask-back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
        <span className="ask-expert-chip">Student Support Channel</span>
        <h1>
          Ask Your Question to
          <span> Speaker & Expert Mentors</span>
        </h1>
        <p>
          Stuck on a concept, project issue, or interview problem? Submit your question here.
          You can stay anonymous and still get reviewed by our expert team.
        </p>
      </section>

      <section className="ask-expert-shell">
        <article className="ask-form-card theme-form-surface" aria-labelledby="ask-form-title">
          <h2 id="ask-form-title">Submit Your Question</h2>
          <p className="ask-form-sub">Anonymous mode is enabled by default.</p>

          {notice.text && (
            <div className={`ask-notice ${notice.kind}`} role="status">
              {notice.text}
            </div>
          )}

          <form onSubmit={submitQuestion} className="ask-form">
            <label htmlFor="question">Your Question</label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Explain your doubt in detail so experts can help clearly..."
              minLength={15}
              maxLength={800}
              required
            />

            <label className="ask-checkbox" htmlFor="isAnonymous">
              <input
                id="isAnonymous"
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
              />
              Submit anonymously
            </label>

            {!isAnonymous && (
              <>
                <label htmlFor="studentName">Your Name</label>
                <input
                  id="studentName"
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Enter your name"
                  required={!isAnonymous}
                />

                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required={!isAnonymous}
                />
              </>
            )}

            <label htmlFor="contact">Phone (Optional)</label>
            <input
              id="contact"
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="10-15 digits"
            />

            <button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Question'}
            </button>
          </form>
        </article>

        <aside className="ask-courses-card" aria-labelledby="course-explore-title">
          <h2 id="course-explore-title">Turn Questions Into Skills</h2>
          <p>
            If your doubts are frequent, structured mentorship can accelerate your growth.
            Explore our placement-focused programs and join the right track.
          </p>

          <div className="ask-course-pills" role="list" aria-label="Featured courses">
            <span role="listitem">React.js + Next.js Fullstack</span>
            <span role="listitem">JavaScript Core to Advanced</span>
            <span role="listitem">AWS Cloud with DevOps</span>
            <span role="listitem">AI-Assisted Development</span>
          </div>

          <div className="ask-course-actions">
            <button
              type="button"
              onClick={async () => {
                await saveMetricEvent('explore_courses_click', 'Explore Courses clicked');
                navigate('/courses');
              }}
            >
              Explore Courses
            </button>
            <button
              type="button"
              className="ghost"
              onClick={async () => {
                await saveMetricEvent('enroll_consultation_click', 'Enroll Consultation clicked');
                navigate('/?fromEnroll=true#enroll-form');
              }}
            >
              Enroll Consultation
            </button>
          </div>
        </aside>
      </section>
    </main>
  );
};

export default AskExpertPage;
